import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useOutletContext, useParams, useNavigate } from 'react-router-dom'
import {
  ComposedChart, Area, Line, XAxis, YAxis, ReferenceLine, ReferenceDot,
  Tooltip, ResponsiveContainer,
} from 'recharts'
import { useI18n } from 'gunsi-auth'
import { pscObjectToString, pscToGregorian } from '@justhyur/lil-calendar'
import { useTally, useRangeTransactions } from '../hooks/useTally.jsx'
import { useTallyI18n } from '../i18n/tally.js'
import { formatAmount, formatDay, expandAllSlices, displayDate, dayKey } from '../format.js'
import { MonthPicker } from '../components/MonthPicker.jsx'
import {
  todayLil, shiftLilMonth, lilMonthRange, formatLilMonthJsx, formatLilYearJsx,
  lilToHe, heToLil,
} from '../lil.jsx'

// ── Overview: a month (or a year) as a story ───────────────────────────────
// The CURRENT month answers "how will it end", top to bottom:
//   how am I doing?        → two tiles: saved so far (fact), month end (estimate)
//   where am I heading?    → the month curve: cumulative net, solid up to today,
//                            dashed projection to the end
//   what should I change?  → the pace bar: everyday spending vs daily income
//   how do you know?       → the breakdown + method note
// A PAST month, or a year, answers "how did it go": the result, the curve
// without projection, and how much of the income was kept. A year never
// projects: future income isn't recorded yet (a Ledger quota reaches Tally
// only once it's sent), so a year-end estimate would miss months of income
// and come out falsely negative. It shows the average month instead.
//
// Every transaction counts on its EFFECTIVE date (displayDate), the same day
// Movimenti and Statistiche put it on, so the three views agree on a month. A
// cash view (payment date) was tried and dropped: Tally keeps no account
// balance, so "how much money do I have" was never its question, while two
// different totals for the same month were a real cost. A flight paid today
// for March therefore weighs on March; its instalments, if split, each on
// their own day.
//
// Projection = what is CERTAIN (everything recorded in the month, scheduled
// ones included) + an ESTIMATE of everyday spending for the days left.
//
// The estimate uses a "typical week": the median of the last 4 complete weeks
// before today, divided by 7. Weeks, not days, on purpose — trimming the
// priciest days would cut a weekly grocery run and halve the estimate for
// anyone who shops that way, while a week with a flight or the rent just ends
// up at the edge of the median and doesn't count. Instalments (slices of a
// split) are left out of the pace: they're scheduled costs, already in the
// month's recorded total — feeding them in too would count them twice.
//
// The viewed period lives in the URL (/dashboard/<year>[/<month>], year in the
// active era, month 1..12 / 1..13 / 'S' for the Lil special days). The URL is
// the only source: the shared viewed month (Movimenti/Statistiche) follows it,
// never the other way round, so the two can't ping-pong. No period in the URL
// → the month already viewed elsewhere.

const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII', 'XIII']
const pad = (n) => String(n).padStart(2, '0')
const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
function addDaysIso(iso, n) {
  const [y, m, d] = iso.split('-').map(Number)
  return isoOf(new Date(y, m - 1, d + n))
}
function daysBetween(a, b) {
  const [ay, am, ad] = a.split('-').map(Number)
  const [by, bm, bd] = b.split('-').map(Number)
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000)
}
function median(sorted) {
  const n = sorted.length
  if (!n) return null
  return n % 2 ? sorted[(n - 1) / 2] : (sorted[n / 2 - 1] + sorted[n / 2]) / 2
}
function shiftYearMonth(ym, delta) {
  const [y, m] = ym.split('-').map(Number)
  const d = new Date(y, m - 1 + delta, 1)
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`
}
const langOf = (locale) => ((locale || 'en').startsWith('it') ? 'it' : 'en')
function gregMonthLabel(ym, locale) {
  const [y, m] = ym.split('-').map(Number)
  return new Intl.DateTimeFormat(langOf(locale), { month: 'long', year: 'numeric' }).format(new Date(y, m - 1, 1))
}
const lilDayIso = (year, month) => String(pscToGregorian(pscObjectToString({ year, month, day: 1 }))).slice(0, 10)
const fill = (s, vars) => Object.entries(vars).reduce((acc, [k, v]) => acc.replaceAll(`{${k}}`, String(v)), s)

// The day a transaction (or a slice of a split) counts on: its effective
// date, exactly as Movimenti groups it.
const dayOf = (tx) => dayKey(displayDate(tx))

// URL params → viewed period, or null when there's none (or it's malformed).
//   Lil:       { period, lil: { year } | { year, month } | { year, special } }
//   Gregorian: { period: 'yearly', year } | { period: 'monthly', ym: 'YYYY-MM' }
function parseView(yearParam, monthParam, isLil, yearEra) {
  if (!yearParam) return null
  const y = Number(yearParam)
  if (!Number.isInteger(y)) return null
  if (isLil) {
    const year = yearEra === 'he' ? heToLil(y) : y
    if (!monthParam) return { period: 'yearly', lil: { year } }
    if (/^s$/i.test(monthParam)) return { period: 'monthly', lil: { year, special: true } }
    const m = Number(monthParam)
    if (!Number.isInteger(m) || m < 1 || m > 13) return null
    return { period: 'monthly', lil: { year, month: m } }
  }
  if (!monthParam) return { period: 'yearly', year: y }
  const m = Number(monthParam)
  if (!Number.isInteger(m) || m < 1 || m > 12) return null
  return { period: 'monthly', ym: `${y}-${pad(m)}` }
}

const PACE_WEEKS = 4
const ANIM_MS = 150

export function Overview() {
  const t = useTallyI18n()
  const o = t.overview
  const { locale } = useI18n()
  const { subTopbar } = useOutletContext()
  const navigate = useNavigate()
  const params = useParams()
  const {
    data,
    viewedGreg: viewedMonth, setViewedGreg: setViewedMonth,
    viewedLil, setViewedLil,
  } = useTally()
  const isLil = data.calendar === 'lil'
  const yearEra = data.yearEra ?? 'lil'
  const cur = data.currency
  const money = (v) => formatAmount(v, cur)
  const todayIso = isoOf(new Date())
  const [pickerOpen, setPickerOpen] = useState(false)

  // ── Viewed period (from the URL, see header) ──────────────────────────
  const view = useMemo(
    () => parseView(params.year, params.month, isLil, yearEra),
    [params.year, params.month, isLil, yearEra],
  )
  const period = view?.period ?? 'monthly'

  const eraYear = (ly) => (yearEra === 'he' ? lilToHe(ly) : ly)
  const pathLilMonth = (v) => `/dashboard/${eraYear(v.year)}/${v.special ? 'S' : v.month}`
  const pathGregMonth = (ym) => `/dashboard/${Number(ym.slice(0, 4))}/${Number(ym.slice(5, 7))}`
  const pathYear = (v) => `/dashboard/${isLil ? eraYear(v.lil.year) : v.year}`
  // replace: the back button leaves the Overview instead of walking through
  // every month browsed.
  const go = (path) => { if (path && window.location.pathname !== path) navigate(path, { replace: true }) }

  // No period in the URL → open on the month already viewed elsewhere.
  useEffect(() => {
    if (view) return
    go(isLil ? pathLilMonth(viewedLil) : pathGregMonth(viewedMonth))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view])

  // The shared viewed month follows the URL (one way only). A year keeps the
  // month already chosen, so switching back to "Monthly" lands on it.
  useEffect(() => {
    if (!view) return
    if (isLil) {
      const tgt = view.lil
      if (view.period === 'yearly') {
        setViewedLil(v => (v.year === tgt.year ? v : { year: tgt.year, month: v.special ? 1 : (v.month ?? 1) }))
      } else {
        setViewedLil(v => (v.year === tgt.year && !!v.special === !!tgt.special && v.month === tgt.month ? v : tgt))
      }
    } else if (view.period === 'yearly') {
      setViewedMonth(v => (v.startsWith(`${view.year}-`) ? v : `${view.year}-${v.split('-')[1] ?? '01'}`))
    } else {
      setViewedMonth(view.ym)
    }
  }, [view, isLil, setViewedLil, setViewedMonth])

  // [from, to) of the viewed period (Gregorian ISO days) + the month starts,
  // used as ticks on the yearly curve.
  const { pFrom, pTo, monthStarts } = useMemo(() => {
    if (!view) return { pFrom: null, pTo: null, monthStarts: [] }
    if (view.period === 'monthly') {
      if (isLil) {
        const r = lilMonthRange(view.lil)
        return { pFrom: r.from && String(r.from).slice(0, 10), pTo: r.to && String(r.to).slice(0, 10), monthStarts: [] }
      }
      const [y, m] = view.ym.split('-').map(Number)
      return { pFrom: `${view.ym}-01`, pTo: isoOf(new Date(y, m, 1)), monthStarts: [] }
    }
    if (isLil) {
      const y = view.lil.year
      const monthStarts = []
      for (let m = 1; m <= 13; m++) monthStarts.push({ iso: lilDayIso(y, m), label: ROMAN[m] })
      return { pFrom: lilDayIso(y, 1), pTo: lilDayIso(y + 1, 1), monthStarts }
    }
    const y = view.year
    const fmt = new Intl.DateTimeFormat(langOf(locale), { month: 'short' })
    const monthStarts = []
    for (let m = 1; m <= 12; m++) {
      monthStarts.push({ iso: `${y}-${pad(m)}-01`, label: fmt.format(new Date(y, m - 1, 1)).replace('.', '') })
    }
    return { pFrom: `${y}-01-01`, pTo: `${y + 1}-01-01`, monthStarts }
  }, [view, isLil, locale])

  // Never the future (same rule as Statistiche): a period that hasn't started
  // snaps to the current one — e.g. a month browsed ahead in Movimenti.
  useEffect(() => {
    if (!view || !pFrom || pFrom <= todayIso) return
    if (view.period === 'yearly') {
      go(`/dashboard/${isLil ? eraYear(todayLil().year) : Number(todayIso.slice(0, 4))}`)
    } else {
      go(isLil ? pathLilMonth(todayLil()) : pathGregMonth(todayIso.slice(0, 7)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, pFrom, todayIso])

  const canGoNext = !!pTo && pTo <= todayIso
  function targetPath(delta) {
    if (!view) return null
    if (view.period === 'yearly') return `/dashboard/${(isLil ? eraYear(view.lil.year) : view.year) + delta}`
    return isLil ? pathLilMonth(shiftLilMonth(view.lil, delta)) : pathGregMonth(shiftYearMonth(view.ym, delta))
  }
  const lastNavDirRef = useRef(null) // 'next' | 'prev' → slide-in direction
  function shiftViewed(delta) {
    if (delta > 0 && !canGoNext) return
    lastNavDirRef.current = delta > 0 ? 'next' : 'prev'
    go(targetPath(delta))
  }
  function changePeriod(next) {
    if (!view || next === period) return
    if (next === 'yearly') {
      go(isLil ? pathYear({ lil: { year: view.lil.year } }) : `/dashboard/${Number(view.ym.slice(0, 4))}`)
    } else {
      go(isLil ? pathLilMonth(viewedLil) : pathGregMonth(viewedMonth))
    }
  }

  // ── Swipe between periods (same feel as Statistiche) ──────────────────
  const stripRef = useRef(null)
  const navRef = useRef({ canGoNext, targetPath })
  useEffect(() => { navRef.current = { canGoNext, targetPath } })

  useLayoutEffect(() => {
    const strip = stripRef.current
    const dir = lastNavDirRef.current
    if (!strip || !dir) return
    lastNavDirRef.current = null
    const w = strip.offsetWidth || window.innerWidth
    strip.style.transition = 'none'
    strip.style.transform = `translateX(${dir === 'next' ? w : -w}px)`
    void strip.offsetWidth
    strip.style.transition = `transform ${ANIM_MS}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
    strip.style.transform = 'translateX(0)'
  }, [pFrom, period])

  useEffect(() => {
    const strip = stripRef.current
    if (!strip) return
    let startX = null, startY = null, dir = null, dx = 0, busy = false
    const ease = `transform ${ANIM_MS}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`

    function onStart(e) {
      // The chart scrubs its tooltip with a horizontal drag: leave it alone.
      if (busy || e.target.closest?.('.ov-chart')) { startX = null; return }
      startX = e.touches[0].clientX
      startY = e.touches[0].clientY
      dir = null
      dx = 0
      strip.style.transition = 'none'
    }
    function onMove(e) {
      if (startX == null || busy) return
      const ddx = e.touches[0].clientX - startX
      const ddy = e.touches[0].clientY - startY
      if (dir === null) {
        if (Math.abs(ddx) > 8 || Math.abs(ddy) > 8) dir = Math.abs(ddx) >= Math.abs(ddy) ? 'h' : 'v'
        return
      }
      if (dir === 'h') {
        if (e.cancelable) e.preventDefault()
        dx = ddx
        strip.style.transform = `translateX(${dx}px)`
      }
    }
    function onEnd() {
      if (startX == null || busy) return
      startX = null
      const goNext = dx < 0
      if (dir !== 'h' || Math.abs(dx) < 50 || (goNext && !navRef.current.canGoNext)) {
        strip.style.transition = ease
        strip.style.transform = 'translateX(0)'
        return
      }
      busy = true
      const w = strip.offsetWidth || window.innerWidth
      strip.style.transition = ease
      strip.style.transform = `translateX(${goNext ? -w : w}px)`
      setTimeout(() => {
        lastNavDirRef.current = goNext ? 'next' : 'prev'
        const path = navRef.current.targetPath(goNext ? 1 : -1)
        if (path) navigate(path, { replace: true })
        busy = false
      }, ANIM_MS)
    }
    strip.addEventListener('touchstart', onStart, { passive: true })
    strip.addEventListener('touchmove', onMove, { passive: false })
    strip.addEventListener('touchend', onEnd)
    return () => {
      strip.removeEventListener('touchstart', onStart)
      strip.removeEventListener('touchmove', onMove)
      strip.removeEventListener('touchend', onEnd)
    }
  }, [navigate])

  // ── Numbers ───────────────────────────────────────────────────────────
  // Everything, once: the pace window (4 weeks before today) crosses the month
  // boundary and a year spans 12–13 months, so one fetch beats a per-period
  // one. Same cache entry as Statistiche → "All".
  const allView = useRangeTransactions('all', '1900-01-01', '9999-12-31')

  const paceFrom   = addDaysIso(todayIso, -7 * PACE_WEEKS)
  const isPast     = !!pTo && todayIso >= pTo
  const projecting = period === 'monthly' && !isPast

  const s = useMemo(() => {
    if (!pFrom || !pTo) return null
    const days      = Math.max(1, daysBetween(pFrom, pTo))
    const elapsed   = isPast ? days : Math.min(days, Math.max(1, daysBetween(pFrom, todayIso) + 1))
    const remaining = days - elapsed

    const slices = expandAllSlices(allView.transactions)

    // Per-day money in / out in the period, by effective day (scheduled
    // future ones included). `dataStart` = first day with any money at all.
    const incByDay = new Array(days).fill(0)
    const expByDay = new Array(days).fill(0)
    const events   = new Array(days).fill(0) // # of scheduled payments per day
    let count = 0
    let dataStart = null
    for (const tx of slices) {
      if (tx.type !== 'income' && tx.type !== 'expense') continue
      const k = dayOf(tx)
      if (!k) continue
      if (dataStart === null || k < dataStart) dataStart = k
      if (k < pFrom || k >= pTo) continue
      const v = tx.amount_base ?? tx.amount ?? 0
      const i = daysBetween(pFrom, k)
      count++
      if (tx.type === 'income') incByDay[i] += v
      else expByDay[i] += v
      if (k > todayIso) events[i]++
    }
    const sum = (arr, from, to) => { let x = 0; for (let i = from; i < to; i++) x += arr[i]; return x }
    const incAll   = sum(incByDay, 0, days),    expAll   = sum(expByDay, 0, days)
    const incSoFar = sum(incByDay, 0, elapsed), expSoFar = sum(expByDay, 0, elapsed)

    // Typical week → daily pace (see header comment). Current month only.
    let dailyPace = null
    if (projecting) {
      const blocks = new Array(PACE_WEEKS).fill(0)
      for (const tx of slices) {
        // Everyday spending only: no instalments, and nothing whose effective
        // day differs from its payment day (a flight booked for March, a share
        // settled later) — planned one-offs that would swamp the median.
        if (tx.type !== 'expense' || tx._parentId) continue
        if (tx.effectiveDate && dayKey(tx.effectiveDate) !== dayKey(tx.date)) continue
        const k = dayOf(tx)
        if (!k || k < paceFrom || k >= todayIso) continue
        const idx = Math.floor(daysBetween(paceFrom, k) / 7)
        if (idx >= 0 && idx < PACE_WEEKS) blocks[idx] += tx.amount_base ?? tx.amount ?? 0
      }
      // An empty block almost always means "no data yet", not a week of zero
      // spending, so it's left out instead of dragging the median to 0.
      const nonEmpty = blocks.filter(b => b > 0).sort((a, b) => a - b)
      const typicalWeek = nonEmpty.length >= 2 ? median(nonEmpty) : null
      dailyPace = typicalWeek != null ? typicalWeek / 7 : null
    }
    const estimate = dailyPace != null ? dailyPace * remaining : 0
    const projNet  = incAll - (expAll + estimate)

    // Average month (yearly view): net per day over the days that have data
    // (a year you started tracking in August isn't diluted by empty months),
    // times the length of an average month of the calendar (13 Lil, 12 Greg).
    const elapsedEnd = addDaysIso(pFrom, elapsed)
    const avgStart   = dataStart && dataStart > pFrom ? dataStart : pFrom
    const avgDays    = daysBetween(avgStart, elapsedEnd)
    const avgMonth   = avgDays > 0 ? ((incSoFar - expSoFar) / avgDays) * (days / (isLil ? 13 : 12)) : null

    // The curve: cumulative net, one point per day. `actual` stops at today,
    // `proj` (current month only) starts at today, so the two segments join,
    // and adds the pace.
    const curve = []
    let acc = 0
    for (let i = 0; i < days; i++) {
      acc += incByDay[i] - expByDay[i]
      const done = i < elapsed
      if (projecting && !done && dailyPace != null) acc -= dailyPace
      curve.push({
        day: i + 1,
        iso: addDaysIso(pFrom, i),
        actual: done ? acc : null,
        proj: projecting && i >= elapsed - 1 ? acc : null,
        event: projecting && events[i] > 0 ? acc : null,
      })
    }

    return {
      incAll, expAll, incSoFar, expSoFar, savedSoFar: incSoFar - expSoFar,
      dailyPace, dailyIncome: incAll / days,
      remaining, estimate, projNet, days, elapsed, count, curve, avgMonth,
    }
  }, [allView.transactions, pFrom, pTo, paceFrom, todayIso, isPast, projecting, isLil])

  // ── Display ───────────────────────────────────────────────────────────
  const loading = allView.loading || !view // !view = redirecting to a period
  const signed = (v) => `${v < 0 ? '−' : ''}${money(Math.abs(v))}`
  const tone   = (v) => (v < 0 ? 'amount-expense' : 'amount-income')
  const toneColor = (v) => (v < 0 ? 'var(--g-expense)' : 'var(--g-income)')

  const periodLabel = !view ? '' : view.period === 'yearly'
    ? (isLil ? formatLilYearJsx(view.lil.year, yearEra) : String(view.year))
    : (isLil ? formatLilMonthJsx(view.lil, yearEra) : gregMonthLabel(view.ym, locale))

  // Pace bar (current month): daily income is the full width; spending fills it.
  const paceRatio = s?.dailyPace != null && s.dailyIncome > 0 ? s.dailyPace / s.dailyIncome : null
  const paceOver  = paceRatio != null && paceRatio > 1
  // Balance bar (past month, year): same bar, with what was actually spent.
  const balRatio = s && !projecting && s.incSoFar > 0 ? s.expSoFar / s.incSoFar : null
  const balOver  = balRatio != null && balRatio > 1

  const curveTitle = projecting ? o.curveTitle
    : period === 'monthly' ? o.curveMonth
    : isPast ? o.curveYear : o.curveYearSoFar

  // X ticks: first / today / last day for a month; month starts for a year.
  const tickLabels = useMemo(() => {
    const m = new Map()
    if (!pFrom) return m
    for (const ms of monthStarts) m.set(daysBetween(pFrom, ms.iso) + 1, ms.label)
    return m
  }, [monthStarts, pFrom])
  const xTicks = !s ? [] : period === 'yearly'
    ? [...tickLabels.keys()]
    : [1, isPast ? null : s.elapsed, s.days].filter((v, i, a) => v != null && a.indexOf(v) === i)

  const CurveTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null
    const p = payload[0].payload
    const v = p.actual ?? p.proj
    if (v == null) return null
    const isEstimate = p.actual == null
    return (
      <div className="ov-tip">
        <div className="ov-tip-date">{formatDay(p.iso, data.dateFormat, locale)}</div>
        <div className={`ov-tip-val ${tone(v)}`}>{isEstimate ? '≈ ' : ''}{signed(v)}</div>
      </div>
    )
  }

  const stickyContent = (
    <div className="stats-sticky-head">
      {/* period-select (left) · month-nav (center) · empty cell (right) */}
      <header className="view-header view-header-month">
        <select className="period-select" value={period} onChange={e => changePeriod(e.target.value)}>
          <option value="monthly">{t.stats.monthly}</option>
          <option value="yearly">{t.stats.yearly}</option>
        </select>
        <div className="month-nav">
          <button className="month-nav-arrow" onClick={() => shiftViewed(-1)} aria-label="Previous">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            className={`month-nav-label${period === 'monthly' && isLil && view?.lil?.special ? ' is-range' : ''}`}
            onClick={() => period === 'monthly' && setPickerOpen(true)}
          >
            {periodLabel}
          </button>
          <button className="month-nav-arrow" onClick={() => shiftViewed(+1)} disabled={!canGoNext} aria-label="Next">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>
        <span aria-hidden="true" />
      </header>
    </div>
  )

  return (
    <>
      {subTopbar && createPortal(stickyContent, subTopbar)}
      <div className="view view-overview" ref={stripRef}>
        {s && !isPast && (
          <header className="ov-head">
            <div className="ov-progress" aria-hidden="true">
              <div className="ov-progress-fill" style={{ width: `${(s.elapsed / s.days) * 100}%` }} />
            </div>
            <span className="ov-progress-label">{fill(o.dayOf, { day: s.elapsed, total: s.days })}</span>
          </header>
        )}

        {loading ? (
          <div className="view-empty"><div className="loading-spinner" /></div>
        ) : !s || (s.count === 0 && s.dailyPace == null) ? (
          <div className="view-empty">
            <span>{t.dashboard.empty}</span>
            <span className="ov-muted">{t.dashboard.emptyHint}</span>
          </div>
        ) : (
          <>
            {/* 1 · how am I doing / how did it go? */}
            {projecting ? (
              <div className="ov-tiles">
                <div className="card ov-tile">
                  <span className="ov-tile-label">{o.soFar}</span>
                  <span className={`ov-tile-val ${tone(s.savedSoFar)}`}>{signed(s.savedSoFar)}</span>
                  <span className="ov-tile-sub">{o.saved}</span>
                </div>
                <div className="card ov-tile ov-tile-est">
                  <span className="ov-tile-label">{o.monthEnd}</span>
                  <span className={`ov-tile-val ${tone(s.projNet)}`}>≈ {signed(s.projNet)}</span>
                  <span className="ov-tile-sub">{o.estimate}</span>
                </div>
              </div>
            ) : period === 'monthly' ? (
              <div className="ov-tiles ov-tiles-one">
                <div className="card ov-tile">
                  <span className="ov-tile-label">{o.monthResult}</span>
                  <span className={`ov-tile-val ${tone(s.savedSoFar)}`}>{signed(s.savedSoFar)}</span>
                  <span className="ov-tile-sub">{o.resultSub}</span>
                </div>
              </div>
            ) : (
              <div className="ov-tiles">
                <div className="card ov-tile">
                  <span className="ov-tile-label">{isPast ? o.yearResult : o.soFar}</span>
                  <span className={`ov-tile-val ${tone(s.savedSoFar)}`}>{signed(s.savedSoFar)}</span>
                  <span className="ov-tile-sub">{isPast ? o.resultSub : o.savedYear}</span>
                </div>
                <div className="card ov-tile">
                  <span className="ov-tile-label">{o.perMonth}</span>
                  <span className={`ov-tile-val ${tone(s.avgMonth ?? 0)}`}>{s.avgMonth != null ? signed(s.avgMonth) : '—'}</span>
                  <span className="ov-tile-sub">{o.perMonthSub}</span>
                </div>
              </div>
            )}

            {/* 2 · the curve */}
            <section className="card ov-card">
              <div className="ov-card-head">
                <h2>{curveTitle}</h2>
                <span className="ov-muted">{o.curveSub}</span>
              </div>
              <div className="ov-chart">
                <ResponsiveContainer width="100%" height={190}>
                  <ComposedChart data={s.curve} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="ovFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%"   stopColor="var(--g-accent)" stopOpacity={0.28} />
                        <stop offset="100%" stopColor="var(--g-accent)" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="day"
                      tick={{ fontSize: 10, fill: 'var(--g-text-muted)' }}
                      tickLine={false} axisLine={false}
                      ticks={xTicks}
                      interval={period === 'yearly' ? 'preserveStartEnd' : 'preserveEnd'}
                      tickFormatter={period === 'yearly' ? (d) => tickLabels.get(d) ?? '' : undefined}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: 'var(--g-text-muted)' }}
                      tickLine={false} axisLine={false} width={44}
                      tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 100) / 10}k` : String(Math.round(v)))}
                    />
                    <Tooltip content={<CurveTooltip />} cursor={{ stroke: 'var(--g-border)', strokeWidth: 1 }} />
                    <ReferenceLine y={0} stroke="var(--g-text-muted)" strokeOpacity={0.6} />
                    {!isPast && <ReferenceLine x={s.elapsed} stroke="var(--g-border)" strokeDasharray="2 3" />}
                    <Area type="monotone" dataKey="actual" stroke="none" fill="url(#ovFill)" baseValue={0} isAnimationActive={false} />
                    <Line type="monotone" dataKey="actual" stroke="var(--g-accent)" strokeWidth={2.5} dot={false} isAnimationActive={false} connectNulls={false} />
                    {projecting && (
                      <Line type="monotone" dataKey="proj" stroke="var(--g-accent)" strokeWidth={2} strokeDasharray="5 4" strokeOpacity={0.7} dot={false} isAnimationActive={false} connectNulls={false} />
                    )}
                    {projecting && (
                      <Line type="monotone" dataKey="event" stroke="none" dot={{ r: 3.5, fill: 'var(--g-surface)', stroke: 'var(--g-accent)', strokeWidth: 2 }} isAnimationActive={false} />
                    )}
                    {!isPast && (
                      <ReferenceDot x={s.elapsed} y={s.savedSoFar} r={5} fill="var(--g-accent)" stroke="var(--g-surface)" strokeWidth={2} />
                    )}
                    {projecting && (
                      <ReferenceDot x={s.days} y={s.projNet} r={5} fill={toneColor(s.projNet)} stroke="var(--g-surface)" strokeWidth={2} />
                    )}
                    {isPast && (
                      <ReferenceDot x={s.days} y={s.savedSoFar} r={5} fill={toneColor(s.savedSoFar)} stroke="var(--g-surface)" strokeWidth={2} />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              {projecting && (
                <div className="ov-legend">
                  <span><i className="ov-leg ov-leg-solid" />{o.legendActual}</span>
                  <span><i className="ov-leg ov-leg-dashed" />{o.legendProj}</span>
                  {s.curve.some(p => p.event != null) && (
                    <span><i className="ov-leg ov-leg-dot" />{o.legendEvent}</span>
                  )}
                </div>
              )}
            </section>

            {/* 3 · pace (current month) or balance (past month, year) */}
            {projecting ? (
              s.dailyPace != null && s.remaining > 0 && (
                <section className="card ov-card">
                  <div className="ov-card-head">
                    <h2>{o.paceTitle}</h2>
                    <span className="ov-muted">
                      {s.dailyIncome > 0
                        ? fill(o.paceSub, { spend: money(s.dailyPace), income: money(s.dailyIncome) })
                        : fill(o.paceSubNoIncome, { spend: money(s.dailyPace) })}
                    </span>
                  </div>
                  {paceRatio != null && (
                    <>
                      <div className={`ov-pace${paceOver ? ' over' : ''}`} aria-hidden="true">
                        <div className="ov-pace-fill" style={{ width: `${Math.min(100, paceRatio * 100)}%` }} />
                        {paceOver && <div className="ov-pace-over" style={{ width: `${Math.min(100, (paceRatio - 1) * 100)}%` }} />}
                      </div>
                      <p className={`ov-pace-verdict ${paceOver ? 'amount-expense' : 'amount-income'}`}>
                        {paceOver
                          ? fill(o.paceOver,  { pct: Math.round((paceRatio - 1) * 100) })
                          : fill(o.paceUnder, { pct: Math.round((1 - paceRatio) * 100) })}
                      </p>
                    </>
                  )}
                </section>
              )
            ) : (
              // Past month / year: one card — kept share, then the numbers.
              <section className="card ov-card">
                <div className="ov-card-head">
                  <h2>{o.balanceTitle}</h2>
                </div>
                {balRatio != null ? (
                  <>
                    <div className={`ov-pace${balOver ? ' over' : ''}`} aria-hidden="true">
                      <div className="ov-pace-fill" style={{ width: `${Math.min(100, balRatio * 100)}%` }} />
                      {balOver && <div className="ov-pace-over" style={{ width: `${Math.min(100, (balRatio - 1) * 100)}%` }} />}
                    </div>
                    <p className={`ov-pace-verdict ${balOver ? 'amount-expense' : 'amount-income'}`}>
                      {balOver
                        ? fill(o.balanceOver,  { pct: Math.round((balRatio - 1) * 100) })
                        : fill(o.balanceUnder, { pct: Math.round((1 - balRatio) * 100) })}
                    </p>
                  </>
                ) : (
                  <span className="ov-muted">{o.noIncome}</span>
                )}
                <ul className="ov-lines">
                  <li>
                    <span>{o.income}</span>
                    <span className="amount-income">{money(s.incSoFar)}</span>
                  </li>
                  <li>
                    <span>{o.paidExpenses}</span>
                    <span className="amount-expense">{money(s.expSoFar)}</span>
                  </li>
                  <li className="ov-lines-total">
                    <span>{isPast ? o.result : o.soFar}</span>
                    <span className={tone(s.savedSoFar)}>{signed(s.savedSoFar)}</span>
                  </li>
                </ul>
                <p className="ov-note">{o.cashNote}</p>
              </section>
            )}

            {/* 4 · how do you know? (current month: the projection, line by line) */}
            {projecting && (
              <section className="card ov-card">
                <div className="ov-card-head">
                  <h2>{o.projection}</h2>
                </div>
                <ul className="ov-lines">
                  <li>
                    <span>{o.recordedIncome}</span>
                    <span className="amount-income">{money(s.incAll)}</span>
                  </li>
                  <li>
                    <span>{o.recordedExpenses}</span>
                    <span className="amount-expense">{money(s.expAll)}</span>
                  </li>
                  {s.dailyPace != null && s.remaining > 0 && (
                    <li>
                      <span>
                        {o.estimatedDaily}
                        <small className="ov-muted">
                          {fill(o.estimatedDailyDetail, { pace: money(s.dailyPace), days: s.remaining })}
                        </small>
                      </span>
                      <span className="amount-expense">{money(s.estimate)}</span>
                    </li>
                  )}
                  <li className="ov-lines-total">
                    <span>{o.monthEnd}</span>
                    <span className={tone(s.projNet)}>≈ {signed(s.projNet)}</span>
                  </li>
                </ul>
                <p className="ov-note">{o.cashNote}</p>
                <p className="ov-note">
                  {s.dailyPace == null ? o.noPace : s.remaining === 0 ? o.lastDay : o.paceNote}
                </p>
              </section>
            )}
          </>
        )}
      </div>

      <MonthPicker
        open={pickerOpen && period === 'monthly'}
        onClose={() => setPickerOpen(false)}
        mode={isLil ? 'lil' : 'greg'}
        viewed={isLil ? (view?.lil ?? viewedLil) : (view?.ym ?? viewedMonth)}
        onSelect={(target) => go(isLil ? pathLilMonth(target) : pathGregMonth(target))}
        yearEra={yearEra}
        locale={locale}
        t={t}
        disableFuture
      />
    </>
  )
}
